
import { Component, Vue, Emit, Watch } from "vue-property-decorator";
import FormAlignmentLine from "@/components/form/FormAlignmentLine/FormAlignmentLine.vue";
import FormAlignmentLineLoggedIn from "@/components/form/FormAlignmentLineLoggedIn/FormAlignmentLineLoggedIn.vue";
import FormAlignmentMGHRWC from "@/components/form/FormAlignmentMGHRWC/FormAlignmentMGHRWC.vue";
import BoxError from "@/components/box/BoxError/BoxError.vue";
import store from "@/store";
import {
  fetchLineLogin,
  fetchLineFriendship,
  fetchLineRegister,
} from "@/services/userService";

Component.registerHooks(["beforeRouteLeave"]);

const systemErrorObj = {
  title: "システムエラー",
  message:
    "サーバーからの応答が無いため処理を実行できませんでした。<br />お手数ですが、しばらく時間が経った後に、再度お試しをお願いします。",
  button: "top",
};

@Component({
  components: {
    BoxError,
    FormAlignmentLine,
    FormAlignmentLineLoggedIn,
    FormAlignmentMGHRWC,
  },
})
export default class LineAuth extends Vue {
  private userId: string = "";
  private error: {
    title: string;
    message: string;
    button: string | null;
  } | null = null;
  private lineAccessToken: string = "";
  private from: string | null = null;
  private viewState = 0;
  private allowLeaveOnNextNavigation = false;
  get lineFriendshipUrl() {
    this.clearStorege();
    const url = this.generateLineAuthUrl() + "&prompt=consent";
    return url;
  }

  get errorTitle(): string {
    return (this.error && this.error.title) || "";
  }

  get errorMessage(): string {
    return (this.error && this.error.message) || "";
  }

  get errorButton(): string | null {
    return (this.error && this.error.button) || null;
  }

  private generateLineAuthUrl(data?: {
    state: string;
    nonce: string;
    redirect_uri: string;
  }) {
    function generateRandomString(length: number): string {
      const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    }

    // const currentUrl = window.location.href;
    const currentUrl = new URL(window.location.href);

    // 特定のクエリを削除
    currentUrl.searchParams.delete("redirect_uri");
    currentUrl.searchParams.delete("state");
    currentUrl.searchParams.delete("code");

    const newUrl = currentUrl.toString();

    // URLエンコード
    const clientId = process.env.VUE_APP_LINE_CLIENT_ID || ""; // LINE Developersで取得
    const randomState = generateRandomString(16); // CSRF対策用ランダム文字列
    const newNonce = generateRandomString(16); // リプレイ攻撃防止用ランダム文字列
    const scope = "profile openid";
    const userAgent = navigator.userAgent || "";
    const isMobile = /iPhone|iPod|Android.+Mobile|Windows Phone/i.test(
      userAgent
    );

    const params: { [key: string]: string } = {
      response_type: "code",
      client_id: clientId,
      redirect_uri: newUrl,
      state: randomState,
      scope: scope,
      nonce: newNonce,
      bot_prompt: "aggressive",
    };
    if (!isMobile) {
      params.initial_amr_display = "lineqr";
    }

    function toQueryString(params: any) {
      return Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join("&");
    }

    const query = toQueryString(params);
    this.setStorege({
      line_state: randomState,
      line_nonce: newNonce,
      line_redirect_uri: data ? data.redirect_uri : encodeURIComponent(newUrl),
    });
    // LINE認可URLを生成
    return `https://access.line.me/oauth2/v2.1/authorize?${query}`;
  }

  @Emit()
  private redirectToLineAuth() {
    const lineAuthUrl = this.generateLineAuthUrl();
    window.location.href = lineAuthUrl;
  }

  @Emit()
  private handlerClickBtn() {
    const token = store.getters["auth/token"];
    this.$router.push({
      path: token ? "/" : "/login",
      replace: true,
    });
  }

  @Emit()
  private updateViewState(num: number) {
    if (this.viewState >= 2 && num < 2) {
      return this.viewState;
    }
    this.viewState = num;
    return this.viewState;
  }

  private allowLeaveWithoutConfirm() {
    this.allowLeaveOnNextNavigation = true;
  }

  private setStorege({
    line_state,
    line_nonce,
    line_redirect_uri,
  }: {
    line_state: string;
    line_nonce: string;
    line_redirect_uri: string;
  }) {
    localStorage.setItem("line_state", line_state);
    localStorage.setItem("line_nonce", line_nonce);
    localStorage.setItem("line_redirect_uri", line_redirect_uri);
  }

  private clearStorege() {
    localStorage.removeItem("line_state");
    localStorage.removeItem("line_redirect_uri");
    localStorage.removeItem("line_nonce");
  }

  private async clickLineButton() {
    store.commit("loaded", false);
    try {
      await fetchLineRegister(this.lineAccessToken);
      await store.dispatch("auth/updateUser");
      this.error = null;
      this.updateViewState(2);
      return;
    } catch (e) {
      // store.commit("loaded", true);
      console.error(e);
      this.error = systemErrorObj;
      store.commit("loaded", true);
    }
  }

  private getStoregeItems(): {
    state: string | null;
    redirect_uri: string | null;
    nonce: string | null;
  } {
    const state = localStorage.getItem("line_state");
    const redirect_uri = localStorage.getItem("line_redirect_uri");
    const nonce = localStorage.getItem("line_nonce");
    return { state, redirect_uri, nonce };
  }

  private async bizLogin(id: string, pass: string) {
    const res = await store.dispatch("auth/login", {
      username: id,
      password: pass,
      action: "token",
      line_id_token: "",
    });
    return res;
  }

  private validateState(state: string) {
    const storegeItems = this.getStoregeItems();
    const savedState = storegeItems.state;
    return savedState === state;
  }

  private getQueryParams(): {
    code: string | null;
    state: string | null;
    from: string | null;
    referrer: string | null;
  } {
    const rawCode = this.$route.query.code;
    const rawState = this.$route.query.state;
    const rawFrom = this.$route.query.from;
    const rawReferrer = this.$route.query.ref;
    const code = Array.isArray(rawCode) ? rawCode[0] : rawCode;
    const state = Array.isArray(rawState) ? rawState[0] : rawState;
    const from = Array.isArray(rawFrom) ? rawFrom[0] : rawFrom;
    const referrer = Array.isArray(rawReferrer) ? rawReferrer[0] : rawReferrer;
    return { code, state, from, referrer };
  }

  private getSafeRedirectPath(from: string | null): string {
    if (!from) {
      return "/";
    }
    const blockedPaths = ["/login", "/line-auth"];
    return blockedPaths.includes(from) ? "/" : from;
  }

  private getBizAccountCredentials(): string {
    const rawAccountBiz = process.env.VUE_APP_ACCOUNT_BIZ || "";
    return rawAccountBiz;
  }

  private getMspAccountCredentials(): string {
    const rawAccountMsp = process.env.VUE_APP_ACCOUNT_MSP || "";
    return rawAccountMsp;
  }

  private getLoopAccountCredentials(): string {
    const rawAccountLoop = process.env.VUE_APP_ACCOUNT_LOOP || "";
    return rawAccountLoop;
  }

  private async mounted() {
    // 1	URLパラメータにcodeまたはstateがない
    // 2	クエリのstateとcodeを取得
    const { code, state, from, referrer } = this.getQueryParams();
    if (
      referrer === "special-favor" ||
      referrer === "msp-lp" ||
      referrer === "loop-lp"
    ) {
      let id = "";
      if (referrer === "special-favor") {
        id = this.getBizAccountCredentials();
      } else if (referrer === "msp-lp") {
        id = this.getMspAccountCredentials();
      } else if (referrer === "loop-lp") {
        id = this.getLoopAccountCredentials();
      }
      if (!id) {
        this.error = systemErrorObj;
        return;
      }
      // 既に指定のアカウントでログインしているかつLINEログインが連携が完了している場合は直接リダイレクト
      if (store.getters["auth/account"] === id && store.getters["auth/line"]) {
        return this.$router.push({
          path: this.getSafeRedirectPath(from),
          replace: true,
        });
      }
      // 既にログインしているアカウントが指定のアカウントでない場合
      if (
        store.getters["auth/account"] &&
        store.getters["auth/account"] !== id
      ) {
        // LINEログインが連携されている場合はLINEログインを解除
        if (store.getters["auth/line"]) {
          await store.dispatch("auth/lineDelete");
        }
        // ログインしているアカウントをログアウト
        await store.dispatch("auth/logout");
      }
    }
    // codeまたはstateがない場合はLINE OAuthを実行
    if (!code || !state) {
      this.redirectToLineAuth();
      return;
    }
    // 3	セッションストレージに保存されているstateとクエリのstateの値が等しくない
    const storegeItems = this.getStoregeItems();
    // 4	セッションストレージにredirect_uriが保存されていない
    if (!this.validateState(state) || !storegeItems.redirect_uri) {
      this.error = systemErrorObj;
      console.error("state or redirect uri is not saved");
      this.clearStorege();
      return;
    }
    let resLineLogin = null;
    // 5	/line/loginにリクエスト
    // 6	LINEアクセストークンとLINE IDトークンを取得
    try {
      // LINE認可コードでLINEアクセストークンとIDトークンを取得
      resLineLogin = await fetchLineLogin({
        code,
        redirect_uri: storegeItems.redirect_uri!,
      }).then(async (data) => {
        if (data.access_token === null || data.id_token === null) {
          throw { code: "errorCode" };
          // await store.dispatch('auth/lineRefresh')
        } else {
          return data;
        }
      });
      // this.clearStorege();
    } catch (e) {
      console.error("fetchLineLogin");
      this.error = systemErrorObj;
      return;
    }

    if (!resLineLogin) {
      this.error = systemErrorObj;
      return;
    }

    // 7	LINE公式アカウントの関係を取得
    try {
      const resLineFriendship = await fetchLineFriendship({
        lineAccessToken: resLineLogin.access_token,
        lineIdToken: resLineLogin.id_token,
      });
      if (!resLineFriendship.friendFlag) {
        this.error = {
          title: "LINE友たち追加が必要です",
          message:
            "以下のボタンより31corp公式LINEアカウントへの友だち追加をお願いします。",
          button: "lineAddFriend",
        };
        this.userId = resLineFriendship.user_id || "";
        return;
      }
    } catch (e) {
      console.error("fetchLineFriendship");
      this.error = systemErrorObj;
      return;
    }

    const token = store.getters["auth/token"];

    if (
      referrer === "special-favor" ||
      referrer === "msp-lp" ||
      referrer === "loop-lp"
    ) {
      let id = "";
      if (referrer === "special-favor") {
        id = this.getBizAccountCredentials();
      } else if (referrer === "msp-lp") {
        id = this.getMspAccountCredentials();
      } else if (referrer === "loop-lp") {
        id = this.getLoopAccountCredentials();
      }
      if (!id) {
        this.error = systemErrorObj;
        return;
      }
      store.commit("temp/setResultLineAuth", "NotLinkedToLine");
      store.commit("temp/setRedirectPayload", resLineLogin);
      this.updateViewState(1);
      return;
    }

    // 8	/token/lineにリクエスト
    try {
      await store.dispatch("auth/autoLogin", {
        line_id_token: resLineLogin.id_token,
      });
      return this.$router.push({
        path: this.getSafeRedirectPath(from),
        replace: true,
      });
    } catch (e: any) {
      if (e.status === 403 && !token) {
        store.commit("temp/setResultLineAuth", "NotLinkedToLine");
        store.commit("temp/setRedirectPayload", resLineLogin);
        this.updateViewState(1);
        return;
      } else if (e.status === 403) {
        this.lineAccessToken = resLineLogin.access_token;
        this.updateViewState(1.5);
        return;
      } else if (e.status === 409) {
        this.error = {
          title: "ご確認ください",
          message: `ご利用のLINE IDには現在ログインしているものとは別の所属先IDが紐づいています。<br />現在ログインしている紐づいている所属先IDに切り替えて、LINEログインを行います。<br /><br />※所属先IDの変更を行う場合は、31corp LINE公式アカウントから31corpコンシェルジュ解除を実施後、再度ご登録をお願いします。`,
          button: "lineLogin",
        };
        this.lineAccessToken = resLineLogin.access_token;
        this.from = from;
        return;
      } else {
        this.error = systemErrorObj;
        return;
      }
    }
  }

  @Watch("error")
  private changeErrorState(newVal: boolean) {
    if (this.error) {
      store.commit("loaded", true);
    }
  }

  @Watch("viewState")
  private changeViewState(newVal: number) {
    store.commit("loaded", true);
  }

  beforeRouteLeave(to: any, from: any, next: any) {
    if (to.name === "error") {
      next();
      return;
    }
    if (this.allowLeaveOnNextNavigation) {
      this.allowLeaveOnNextNavigation = false;
    } else if (this.viewState === 1 || this.viewState === 1.5) {
      const answer = window.confirm(
        "変更が保存されていません。ページを離れますか？"
      );
      if (!answer) {
        store.commit("loaded", true);
        next(false); // 離脱をキャンセル
        return;
      }
      // 同一遷移でガードが再評価されても確認ダイアログを再表示しない
      this.allowLeaveOnNextNavigation = true;
    }
    if (store.state.temp.redirectPayload) {
      store.commit("temp/clearRedirectPayload");
    }
    if (store.state.temp.resultLineAuth) {
      store.commit("temp/clearResultLineAuth");
    }
    this.clearStorege();
    next(); // 離脱許可
  }
}
